import { CertificateMatch } from "../components/LegacyCertList";

type SearchParams = {
  itemId?: string;
  serial?: string;
  assetId?: number;
  blanccoReportUuid?: string;
  legacyLimit?: number;
  custom?: string;
};

export type CertificateResult = {
  blanccoHits: number;
  certusHits: number;
  legacyHits: number;
  matches: CertificateMatch[];
  warnings?: string[];
};

export async function searchCertificates(params: SearchParams): Promise<CertificateResult> {
  const { itemId, serial, assetId, blanccoReportUuid, legacyLimit = 500, custom } = params;

  let blanccoHits = 0;
  let certusHits = 0;
  let legacyHits = 0;
  const matches: CertificateMatch[] = [];
  const warnings: string[] = [];
  const handleError = (provider: string, err: any) => {
    const code = String(err?.code || "").replace(/^functions\//, "");
    if (code === "permission-denied") throw new Error("Du saknar behörighet att hämta certifikatet.");
    if (code === "unauthenticated") throw new Error("Logga in igen för att hämta certifikatet.");
    if (code === "failed-precondition" && err?.message?.includes("Öppna posten på nytt")) throw new Error(err.message);
    if (code === "not-found") return;
    warnings.push(`${provider}: Certifikatet kunde inte hämtas. Försök igen.`);
  };

  // Blancco
  if (assetId !== undefined || blanccoReportUuid) {
    const { fetchBlanccoReport } = await import("./blanccoClient");
    try {
      const resBl = await fetchBlanccoReport(blanccoReportUuid || "", "PDF", assetId, itemId);
      blanccoHits = 1;
      matches.push({
        provider: "blancco",
        label: resBl.filename || "Blancco-rapport",
        uuid: blanccoReportUuid || (assetId !== undefined ? String(assetId) : resBl.filename),
        dataBase64: resBl.dataBase64,
        contentType: resBl.contentType,
        filename: resBl.filename,
        reportDate: null,
        bucketPath: null,
        signedUrl: null,
        serialNormalized: null,
      });
    } catch (err: any) {
      handleError("Blancco", err);
    }
  }

  // Certus
  if (assetId !== undefined) {
    const { fetchCertusReport } = await import("./certusClient");
    try {
      const resCert = await fetchCertusReport(assetId, itemId);
      certusHits = resCert.hits || 0;
      if (certusHits > 0) {
        matches.push({
          provider: "certus",
          label: resCert.filename || "Certus-rapport",
          uuid: resCert.filename,
          dataBase64: resCert.dataBase64,
          contentType: resCert.contentType,
          filename: resCert.filename,
          reportDate: null,
          bucketPath: null,
          signedUrl: null,
          serialNormalized: null,
        });
      }
    } catch (err: any) {
      handleError("Certus", err);
    }
  }

  // Legacy
  if (serial || custom) {
    const { fetchLegacyCertBySerial } = await import("./legacyCertClient");
    try {
      const res = await fetchLegacyCertBySerial(serial, legacyLimit, custom, itemId);
      legacyHits = res.matches.length;
      res.matches.forEach((m) => {
        matches.push({
          provider: "legacy",
          label: m.uuid ?? "Legacy-rapport",
          uuid: m.uuid ?? undefined,
          reportDate: m.reportDate ?? null,
          signedUrl: m.signedUrl ?? null,
          bucketPath: m.bucketPath ?? null,
          serialNormalized: m.serialNormalized ?? null,
        });
      });
    } catch (err) {
      handleError("Certifikatarkivet", err);
    }
  }

  if (!matches.length && warnings.length) throw new Error(warnings.join(" "));

  return {
    warnings,
    blanccoHits,
    certusHits,
    legacyHits,
    matches,
  };
}
